/**
 * Deployment-environment tier.
 * NONPROD = localhost / DEVL / INTG / TEST / CERT
 * PROD    = production
 */
export type RoleEnv = 'NONPROD' | 'PROD';

// ─── Exact IAG group names received from /userdetails ─────────────────────────

/** Non-production IAG roles */
export const FRM_NONPROD_ADMIN    = 'SG-IC1-NONPROD-ADMIN-ROLE';
export const FRM_NONPROD_ANALYST  = 'SG-IC1-NONPROD-ANALYST-ROLE';
export const FRM_NONPROD_READONLY = 'SG-IC1-NONPROD-READONLY-ROLE';

/** Production IAG roles */
export const FRM_PROD_ADMIN    = 'SG-IC1-PROD-ADMIN-ROLE';
export const FRM_PROD_ANALYST  = 'SG-IC1-PROD-ANALYST-ROLE';
export const FRM_PROD_READONLY = 'SG-IC1-PROD-READONLY-ROLE';

// ─── External roles (no NONPROD/PROD split — same string in all environments) ──

/** External Lender: write access to FRM Data; Preparer for Lender Attestation */
export const FRM_LENDER       = 'IC1_MFRM_LENDER_ROLE';
/** External Lender Admin: approves Lender Attestation submissions */
export const FRM_LENDER_ADMIN = 'IC1_MFRM_LENDER_ADMIN_ROLE';
/** External Custodian (US Bank): read-only access to US Bank Custodian */
export const FRM_CUSTODIAN    = 'IC1_MFRM_CUSTODIAN_ROLE';

// ─── Grouped by permission level (cross-env) — for hasRole() spread usage ─────

/** All ADMIN roles across both environments */
export const ADMIN_ROLES    = [FRM_NONPROD_ADMIN,    FRM_PROD_ADMIN]    as const;
/** All ANALYST roles across both environments */
export const ANALYST_ROLES  = [FRM_NONPROD_ANALYST,  FRM_PROD_ANALYST]  as const;
/** All READONLY roles across both environments */
export const READONLY_ROLES = [FRM_NONPROD_READONLY, FRM_PROD_READONLY] as const;
/** All External Lender roles (write access + preparer) */
export const LENDER_ROLES       = [FRM_LENDER]       as const;
/** All External Lender Admin roles (attestation approval) */
export const LENDER_ADMIN_ROLES = [FRM_LENDER_ADMIN] as const;
/** All External Custodian roles (read-only, US Bank) */
export const CUSTODIAN_ROLES    = [FRM_CUSTODIAN]    as const;

// ─── Grouped by environment tier — used by EnvRoleService for env isolation ───

/** All NONPROD roles (any permission level) */
export const NONPROD_ROLES  = [FRM_NONPROD_ADMIN, FRM_NONPROD_ANALYST, FRM_NONPROD_READONLY, FRM_LENDER, FRM_LENDER_ADMIN, FRM_CUSTODIAN] as const;
/** All PROD roles (any permission level) */
export const PROD_ROLES     = [FRM_PROD_ADMIN, FRM_PROD_ANALYST, FRM_PROD_READONLY, FRM_LENDER, FRM_LENDER_ADMIN, FRM_CUSTODIAN] as const;

/** Lookup: RoleEnv → the matching role constant array */
export const ROLES_BY_ENV: Record<RoleEnv, readonly string[]> = {
  NONPROD: NONPROD_ROLES,
  PROD:    PROD_ROLES
};

/** All recognized IAG roles across both environments — use only in low-level auth checks */
export const ALL_IAG_ROLES  = [...ADMIN_ROLES, ...ANALYST_ROLES, ...READONLY_ROLES, ...LENDER_ROLES, ...LENDER_ADMIN_ROLES, ...CUSTODIAN_ROLES] as const;

